import { useEffect, useState } from 'react';
import useFetch from '../hooks/api';
import Select from './Select';
import { KeyRound, MessageSquare, Phone, SendHorizonal, Mail, Smartphone } from 'lucide-react';
import { Button } from '../shadcn/Button';
import Input from './Input';
import Notice from './Notice';
import { validate_phone_number } from '../utils/validate';
import { WhatsApp } from '@material-ui/icons';
import { cn } from '../shadcn/utils';
import PhoneInput from './PhoneInput';

export default function TwoFactorAuth({
	select_phone_numbers,
	country,
	email,
	onlyEmail,
	onSuccess
}: {
	select_phone_numbers?: { country: string; phone_number: string }[];
	country: string;
	email?: string;
	onlyEmail?: boolean;
	onSuccess: (phone_number: string) => void;
}) {
	const [selected2faNumber, setSelected2faNumber] = useState<{ country: string; phone_number: string } | null>(null);

	const [code2fa, setCode2fa] = useState('');
	const [errorMessage2fa, setErrorMessage2fa] = useState('');
	const [cooldown, setCooldown] = useState(0);

	const [request2faMethod, setRequest2faMethod] = useState<'sms' | 'whatsapp' | 'email'>('sms');
	const [request2fa, setRequest2fa] = useState(false);
	const {
		data: request2faResponse,
		setPayload: setRequest2faPayload,
		error: request2faError,
		loading: request2faLoading
	} = useFetch('https://api.krece.app/notifications/requestauthcode/', 'POST', request2fa, undefined, undefined, false);

	const [confirm2fa, setConfirm2fa] = useState(false);
	const {
		data: confirm2faResponse,
		setPayload: setConfirm2faPayload,
		error: confirm2faError,
		loading: confirm2faLoading
	} = useFetch('https://api.krece.app/notifications/confirmauthcode/', 'POST', confirm2fa, undefined, undefined, false);

	useEffect(() => {
		if (request2faError) {
			setErrorMessage2fa('Error enviando token, por favor intente nuevamente.');
		}
	}, [request2faError]);

	useEffect(() => {
		if (confirm2faError) {
			setErrorMessage2fa('Error confirmando token, por favor intente nuevamente.');
			setCode2fa('');
		}
	}, [confirm2faError]);

	// If only one phone number is provided, preselect it and show as a static value
	useEffect(() => {
		if (!selected2faNumber && select_phone_numbers && select_phone_numbers.length === 1) {
			setSelected2faNumber(select_phone_numbers[0]);
		}
	}, [select_phone_numbers, selected2faNumber]);

	useEffect(() => {
		if (code2fa.length === 6 && request2faResponse?.success) {
			setConfirm2fa(true);
			setConfirm2faPayload({
				confirmation_code: parseInt(code2fa),
				country: request2faMethod === 'email' ? selected2faNumber?.country || country : selected2faNumber?.country || ''
			});
		} else if (code2fa.length === 6 && !request2faResponse?.success) {
			setErrorMessage2fa('Todavía no se ha enviado el token, por favor envíelo primero.');
		}
	}, [code2fa, request2faResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (confirm2faResponse?.success) {
			onSuccess(request2faMethod === 'email' && email ? email : selected2faNumber!.phone_number);
		}
	}, [confirm2faResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (cooldown > 0) {
			const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [cooldown]);

	const hasValidPhoneSelection = Boolean(
		selected2faNumber &&
			(validate_phone_number(selected2faNumber.phone_number, selected2faNumber.country) || select_phone_numbers?.length)
	);

	const canProceed = hasValidPhoneSelection || !!email || !!request2faResponse?.success;

	const handleRequestCode = (method: 'sms' | 'whatsapp' | 'email') => {
		if (cooldown > 0) return;

		setRequest2faMethod(method);
		setRequest2fa(true);
		if (method === 'email') {
			setRequest2faPayload({
				code_type: 'email',
				email: email!,
				country
			});
		} else {
			if (!hasValidPhoneSelection) {
				setErrorMessage2fa('Selecciona un número de teléfono válido.');
				setRequest2fa(false);
				return;
			}
			setRequest2faPayload({
				code_type: method,
				phone_number: selected2faNumber!.phone_number,
				country: selected2faNumber!.country
			});
		}
		setCooldown(30);
	};

	return (
		<div className="flex flex-col gap-2">
			{!onlyEmail && select_phone_numbers !== undefined ? (
				select_phone_numbers.length === 1 ? (
					<div className="flex items-center justify-between rounded-md border border-neutral-200 bg-background px-3 py-2 dark:border-neutral-800">
						<div className="flex items-center gap-2">
							<Phone className="size-4 text-neutral-300" />
							<span className="text-sm text-neutral-700 dark:text-neutral-200">{select_phone_numbers[0].phone_number}</span>
						</div>
						<span className="text-xs text-neutral-400">Número de teléfono</span>
					</div>
				) : (
					<Select
						options={select_phone_numbers?.map(({ phone_number }) => ({ label: phone_number, value: phone_number })) || []}
						Icon={<Phone className="size-4 text-neutral-300" />}
						onSelectedChange={(value) =>
							setSelected2faNumber({
								phone_number: value as string,
								country: select_phone_numbers!.find((n) => n.phone_number === value)?.country || country
							})
						}
						placeholder="Selecciona un número"
						floatingLabel="Número de teléfono"
					/>
				)
			) : (
				!onlyEmail && (
					<PhoneInput country={country} onChange={(value) => setSelected2faNumber({ country, phone_number: value })} />
				)
			)}
			{email && (
				<div className="flex items-center justify-between rounded-md border border-neutral-200 bg-background px-3 py-2 dark:border-neutral-800">
					<div className="flex items-center gap-2">
						<Mail className="size-4 text-neutral-300" />
						<span className="text-sm text-neutral-700 dark:text-neutral-200">{email}</span>
					</div>
					<span className="text-xs text-neutral-400">Correo electrónico</span>
				</div>
			)}
			{canProceed && (
				<>
					{!confirm2faResponse?.success && (
						<>
							<p className={cn('select-none text-sm text-neutral-400', { hidden: onlyEmail && !cooldown })}>
								{cooldown > 0
									? `Debe esperar ${cooldown} segundos para enviar otro código...`
									: onlyEmail
										? ''
										: 'Selecciona un método para recibir tu código de verificación.'}
							</p>
							{!onlyEmail && hasValidPhoneSelection && (
								<div className={cn('flex gap-2', { 'mb-3.5': request2faResponse?.success && !confirm2faResponse?.success })}>
									<Button
										loading={request2faLoading && request2faMethod === 'sms'}
										disabled={confirm2faResponse?.success || request2faLoading || cooldown > 0}
										variant="primary"
										className="w-1/2 bg-blue-600 text-white hover:bg-blue-700"
										containerClassName="w-full"
										onClick={() => handleRequestCode('sms')}
									>
										<div className="flex w-full items-center justify-between">
											<div className="flex items-center gap-1">
												<MessageSquare className="relative top-[0.5px] !hidden size-4 text-blue-200 min-[420px]:!inline-flex" />
												<span className="text-blue-50">SMS</span>
											</div>
											<SendHorizonal className="size-4 text-blue-200" />
										</div>
									</Button>
									<Button
										loading={request2faLoading && request2faMethod === 'whatsapp'}
										disabled={confirm2faResponse?.success || request2faLoading || cooldown > 0}
										variant="primary"
										className="w-1/2 bg-green-600 text-white hover:bg-green-700"
										onClick={() => handleRequestCode('whatsapp')}
										containerClassName="w-full"
									>
										<div className="flex w-full items-center justify-between">
											<div className="flex items-center gap-1">
												<WhatsApp className="!hidden !text-[1rem] text-green-200 min-[420px]:!inline-flex" />
												<span className="text-green-50">WhatsApp</span>
											</div>
											<SendHorizonal className="size-4 text-green-200" />
										</div>
									</Button>
								</div>
							)}
							{email && (
								<div className={cn('flex gap-2', { 'mb-3.5': request2faResponse?.success && !confirm2faResponse?.success })}>
									<Button
										loading={request2faLoading && request2faMethod === 'email'}
										disabled={confirm2faResponse?.success || request2faLoading || cooldown > 0}
										variant="primary"
										className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
										containerClassName="w-full"
										onClick={() => handleRequestCode('email')}
									>
										<div className="flex w-full items-center justify-between">
											<div className="flex items-center gap-1">
												<Mail className="!hidden size-4 text-indigo-200 min-[420px]:!inline-flex" />
												<span className="text-indigo-50">{onlyEmail ? 'Enviar código' : 'Email'}</span>
											</div>
											<SendHorizonal className="size-4 text-indigo-200" />
										</div>
									</Button>
								</div>
							)}
						</>
					)}

					{request2faResponse?.success && !confirm2faResponse?.success && (
						<Input
							readonly={confirm2faResponse?.success}
							disabled={confirm2faLoading || confirm2faResponse?.success}
							containerClassName="w-full"
							Icon={<KeyRound className="size-4 text-primary-500" />}
							value={code2fa}
							onChange={(value) => setCode2fa(value)}
							placeholder="Ingresa el código"
							allowedChars="numbers_only"
							floatingLabel="Código de verificación"
						/>
					)}

					{errorMessage2fa && !confirm2faResponse?.success && (
						<Notice message={errorMessage2fa} variant="error" className="mt-1.5" />
					)}
					{confirm2faResponse?.success && <Notice message="Verificación exitosa 👍" variant="success" />}
				</>
			)}
		</div>
	);
}

type CodeType = 'whatsapp' | 'sms' | 'email' | 'in_app';

interface MethodConfig {
	type: CodeType;
	// For email: provide email or allow free entry
	email?: string;
	allowEmailInput?: boolean;
	// For sms/whatsapp: provide phone or allow free entry
	phoneNumber?: string;
	phoneCountry?: string;
	allowPhoneInput?: boolean;
	// For in_app: always requires identification_number
	identificationNumber?: string;
	// Display label for the method button
	label?: string;
}

interface TwoFactorAuthV2Props {
	methods: MethodConfig[];
	country: string;
	onCodeSent?: (method: CodeType, destination: string) => void;
	onSuccess?: (method: CodeType, destination: string, verificationToken?: string) => void;
	/** Issue an authenticated token for profile changes. */
	issueVerificationToken?: boolean;
	confirmCode?: boolean; // If false, only send codes without confirmation
	className?: string;
	/**
	 * 'default' renders the original full-width colored buttons.
	 * 'compact' is a scoped variant for inline contexts (e.g. pay.jsx store 2FA)
	 * — pill-style method picker, slim destination chip, slim send button.
	 * Logic and behavior are identical in both variants.
	 */
	variant?: 'default' | 'compact';
}

/**
 * TwoFactorAuthV2 - A flexible 2FA component that supports multiple authentication methods
 *
 * @example
 * // Email with specific address
 * <TwoFactorAuthV2
 *   methods={[{ type: 'email', email: 'user@example.com' }]}
 *   country="venezuela"
 *   onSuccess={(method, destination) => console.log('Verified!', method, destination)}
 * />
 *
 * @example
 * // Multiple methods with free entry
 * <TwoFactorAuthV2
 *   methods={[
 *     { type: 'sms', allowPhoneInput: true, phoneCountry: 'venezuela' },
 *     { type: 'whatsapp', allowPhoneInput: true, phoneCountry: 'venezuela' },
 *     { type: 'email', allowEmailInput: true }
 *   ]}
 *   country="venezuela"
 *   onSuccess={(method, destination) => console.log('Verified!', method, destination)}
 * />
 *
 * @example
 * // In-app notification (requires identification_number)
 * <TwoFactorAuthV2
 *   methods={[{ type: 'in_app', identificationNumber: '12345678' }]}
 *   country="venezuela"
 *   onSuccess={(method, destination) => console.log('Verified!', method, destination)}
 * />
 *
 * @example
 * // Only send codes without confirmation
 * <TwoFactorAuthV2
 *   methods={[{ type: 'sms', phoneNumber: '+584141234567', phoneCountry: 'venezuela' }]}
 *   country="venezuela"
 *   confirmCode={false}
 *   onCodeSent={(method, destination) => console.log('Code sent!', method, destination)}
 * />
 */
export function TwoFactorAuthV2({
	methods,
	country,
	onCodeSent,
	onSuccess,
	confirmCode = true,
	issueVerificationToken = false,
	className,
	variant = 'default'
}: TwoFactorAuthV2Props) {
	const isCompact = variant === 'compact';
	const [selectedMethod, setSelectedMethod] = useState<MethodConfig | null>(null);
	const [inputEmail, setInputEmail] = useState('');
	const [inputPhone, setInputPhone] = useState('');
	const [code, setCode] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [cooldown, setCooldown] = useState(0);

	const [request2fa, setRequest2fa] = useState(false);
	const {
		data: request2faResponse,
		setPayload: setRequest2faPayload,
		error: request2faError,
		loading: request2faLoading,
		reset: resetRequest2fa
	} = useFetch('https://api.krece.app/notifications/requestauthcode/', 'POST', request2fa, undefined, undefined, false);

	const [confirm2fa, setConfirm2fa] = useState(false);
	const {
		data: confirm2faResponse,
		setPayload: setConfirm2faPayload,
		error: confirm2faError,
		loading: confirm2faLoading
	} = useFetch(
		issueVerificationToken
			? 'https://api.krece.app/notifications/confirmauthcode-token/'
			: 'https://api.krece.app/notifications/confirmauthcode/',
		'POST',
		confirm2fa,
		undefined,
		undefined,
		issueVerificationToken
	);

	useEffect(() => {
		if (request2faError) {
			setErrorMessage('Error enviando el código, por favor intente nuevamente.');
		}
	}, [request2faError]);

	useEffect(() => {
		if (confirm2faError) {
			setErrorMessage('Error confirmando el código, por favor intente nuevamente.');
			setCode('');
		}
	}, [confirm2faError]);

	useEffect(() => {
		if (request2faResponse?.success && selectedMethod) {
			const destination = getDestination();
			onCodeSent?.(selectedMethod.type, destination);

			if (!confirmCode) {
				// If not confirming, call onSuccess immediately after sending
				onSuccess?.(selectedMethod.type, destination);
			}
		}
	}, [request2faResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (confirmCode && code.length === 6 && request2faResponse?.success) {
			setConfirm2fa(true);
			setConfirm2faPayload({
				confirmation_code: parseInt(code),
				country: getCountryForConfirmation()
			});
		} else if (code.length === 6 && !request2faResponse?.success) {
			setErrorMessage('Primero debe enviar el código de verificación.');
		}
	}, [code, request2faResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (confirm2faResponse?.success && selectedMethod) {
			const token = 'verification_token' in confirm2faResponse ? confirm2faResponse.verification_token : undefined;
			if (issueVerificationToken && !token) {
				setErrorMessage('No se pudo verificar el código. Intente nuevamente.');
				setCode('');
				return;
			}
			onSuccess?.(selectedMethod.type, getDestination(), token);
		}
	}, [confirm2faResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (cooldown > 0) {
			const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [cooldown]);

	const getDestination = (): string => {
		if (!selectedMethod) return '';

		switch (selectedMethod.type) {
			case 'email':
				return selectedMethod.email || inputEmail;
			case 'sms':
			case 'whatsapp':
				return selectedMethod.phoneNumber || inputPhone;
			case 'in_app':
				return selectedMethod.identificationNumber || '';
			default:
				return '';
		}
	};

	const getCountryForConfirmation = (): string => {
		if (!selectedMethod) return country;

		switch (selectedMethod.type) {
			case 'sms':
			case 'whatsapp':
				return selectedMethod.phoneCountry || country;
			case 'email':
			case 'in_app':
				return country;
			default:
				return country;
		}
	};

	const canSendCode = (): boolean => {
		if (!selectedMethod) return false;

		switch (selectedMethod.type) {
			case 'email':
				if (selectedMethod.allowEmailInput) {
					return !!inputEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputEmail);
				}
				return !!selectedMethod.email;
			case 'sms':
			case 'whatsapp':
				if (selectedMethod.allowPhoneInput) {
					const phoneCountry = selectedMethod.phoneCountry || country;
					return !!inputPhone && validate_phone_number(inputPhone, phoneCountry);
				}
				return !!selectedMethod.phoneNumber;
			case 'in_app':
				return !!selectedMethod.identificationNumber;
			default:
				return false;
		}
	};

	const handleSendCode = () => {
		if (cooldown > 0 || !selectedMethod || !canSendCode()) return;

		setRequest2fa(true);
		setErrorMessage('');

		const payload: any = {
			code_type: selectedMethod.type,
			country: getCountryForConfirmation()
		};

		switch (selectedMethod.type) {
			case 'email':
				payload.email = selectedMethod.email || inputEmail;
				break;
			case 'sms':
			case 'whatsapp':
				payload.phone_number = selectedMethod.phoneNumber || inputPhone;
				break;
			case 'in_app':
				payload.identification_number = selectedMethod.identificationNumber;
				break;
		}

		setRequest2faPayload(payload);
		setCooldown(30);
	};

	const handleMethodSelect = (method: MethodConfig) => {
		setSelectedMethod(method);
		setErrorMessage('');
		setCode('');
		resetRequest2fa();
		setRequest2fa(false);
		setConfirm2fa(false);
		setInputEmail('');
		setInputPhone('');
	};

	const getMethodIcon = (type: CodeType) => {
		switch (type) {
			case 'sms':
				return <MessageSquare className="size-4" />;
			case 'whatsapp':
				return <WhatsApp className="!text-[1rem]" />;
			case 'email':
				return <Mail className="size-4" />;
			case 'in_app':
				return <Smartphone className="size-4" />;
		}
	};

	const getMethodLabel = (method: MethodConfig) => {
		if (method.label) return method.label;

		switch (method.type) {
			case 'sms':
				return 'SMS';
			case 'whatsapp':
				return 'WhatsApp';
			case 'email':
				return 'Email';
			case 'in_app':
				return 'App';
		}
	};

	const getMethodColor = (type: CodeType) => {
		switch (type) {
			case 'sms':
				return 'blue';
			case 'whatsapp':
				return 'green';
			case 'email':
				return 'indigo';
			case 'in_app':
				return 'purple';
		}
	};

	const renderMethodInput = () => {
		if (!selectedMethod) return null;

		switch (selectedMethod.type) {
			case 'email':
				if (selectedMethod.allowEmailInput) {
					return (
						<Input
							containerClassName="w-full"
							Icon={<Mail className="size-4 text-neutral-300" />}
							value={inputEmail}
							onChange={(value) => setInputEmail(value)}
							placeholder="correo@ejemplo.com"
							floatingLabel="Correo electrónico"
						/>
					);
				} else if (selectedMethod.email) {
					return (
						<div className="flex items-center justify-between rounded-md border border-neutral-200 bg-background px-3 py-2 dark:border-neutral-800">
							<div className="flex items-center gap-2">
								<Mail className="size-4 text-neutral-300" />
								<span className="text-sm text-neutral-700 dark:text-neutral-200">{selectedMethod.email}</span>
							</div>
							<span className="text-xs text-neutral-400">Correo electrónico</span>
						</div>
					);
				}
				break;
			case 'sms':
			case 'whatsapp':
				if (selectedMethod.allowPhoneInput) {
					return <PhoneInput country={selectedMethod.phoneCountry || country} onChange={(value) => setInputPhone(value)} />;
				} else if (selectedMethod.phoneNumber) {
					return (
						<div className="flex items-center justify-between rounded-md border border-neutral-200 bg-background px-3 py-2 dark:border-neutral-800">
							<div className="flex items-center gap-2">
								<Phone className="size-4 text-neutral-300" />
								<span className="text-sm text-neutral-700 dark:text-neutral-200">{selectedMethod.phoneNumber}</span>
							</div>
							<span className="text-xs text-neutral-400">Número de teléfono</span>
						</div>
					);
				}
				break;
			case 'in_app':
				if (selectedMethod.identificationNumber) {
					return (
						<div className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5 dark:border-neutral-800 dark:bg-neutral-900">
							<Smartphone className="size-5 text-purple-500" />
							<div className="flex flex-col">
								<span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
									Notificación push
								</span>
								<span className="text-xs text-neutral-500 dark:text-neutral-400">
									Recibirás el código en tu dispositivo activo
								</span>
							</div>
						</div>
					);
				}
				break;
		}

		return null;
	};

	const codeSentSuccessfully = request2faResponse?.success;
	const codeConfirmedSuccessfully = !!confirm2faResponse?.success && (!issueVerificationToken ||
		('verification_token' in confirm2faResponse && !!confirm2faResponse.verification_token));

	// Compact variant — scoped, opt-in. Used inline (e.g. pay.jsx store 2FA).
	// Default variant below is intentionally untouched.
	if (isCompact) {
		const iconAccent = (type: CodeType) => {
			switch (type) {
				case 'sms':
					return 'text-blue-400';
				case 'whatsapp':
					return 'text-green-400';
				case 'email':
					return 'text-indigo-400';
				case 'in_app':
					return 'text-purple-400';
			}
		};

		return (
			<div className={cn('flex flex-col gap-2.5', className)}>
				{!selectedMethod && (
					<div className="flex flex-wrap gap-1.5">
						{methods.map((method, index) => (
							<button
								key={index}
								type="button"
								onClick={() => handleMethodSelect(method)}
								className="group inline-flex items-center gap-1.5 rounded-full border border-neutral-700/60 bg-neutral-900/40 px-3 py-1.5 text-xs font-medium text-neutral-200 transition-all hover:-translate-y-px hover:border-primary-500/50 hover:bg-primary-500/10 hover:text-neutral-50"
							>
								<span className={cn('flex size-3.5 items-center justify-center', iconAccent(method.type))}>
									{getMethodIcon(method.type)}
								</span>
								<span>{getMethodLabel(method)}</span>
							</button>
						))}
					</div>
				)}

				{selectedMethod && !codeConfirmedSuccessfully && (
					<>
						<div className="flex items-center justify-between gap-2 rounded-md border border-neutral-800/70 bg-neutral-900/40 px-3 py-2">
							<div className="flex min-w-0 items-center gap-2">
								<span className={cn('flex size-4 shrink-0 items-center justify-center', iconAccent(selectedMethod.type))}>
									{getMethodIcon(selectedMethod.type)}
								</span>
								<span className="truncate text-xs text-neutral-200">
									{getDestination() ||
										(selectedMethod.type === 'email'
											? 'Ingresa un correo'
											: selectedMethod.type === 'in_app'
												? 'Notificación push'
												: 'Ingresa un número')}
								</span>
							</div>
							{!codeSentSuccessfully && (
								<button
									type="button"
									onClick={() => handleMethodSelect(null as any)}
									className="shrink-0 text-xs text-neutral-400 transition-colors hover:text-neutral-100"
								>
									Cambiar
								</button>
							)}
						</div>

						{(selectedMethod.allowEmailInput || selectedMethod.allowPhoneInput) && !codeSentSuccessfully && renderMethodInput()}

						{!codeSentSuccessfully && (
							<Button
								variant="primary"
								loading={request2faLoading}
								disabled={!canSendCode() || cooldown > 0 || request2faLoading}
								onClick={handleSendCode}
								className="!h-9 !py-0 !text-xs"
							>
								<SendHorizonal className="size-3.5" />
								<span>{cooldown > 0 ? `Reintentar en ${cooldown}s` : 'Enviar código'}</span>
							</Button>
						)}

						{confirmCode && codeSentSuccessfully && !codeConfirmedSuccessfully && (
							<>
								<Input
									readonly={codeConfirmedSuccessfully}
									disabled={confirm2faLoading || codeConfirmedSuccessfully}
									containerClassName="w-full"
									Icon={<KeyRound className="size-4 text-primary-500" />}
									value={code}
									onChange={(value) => setCode(value)}
									placeholder="Ingresa el código"
									allowedChars="numbers_only"
									floatingLabel="Código de verificación"
									maxLength={6}
								/>
								{cooldown <= 0 && (
									<button
										type="button"
										onClick={handleSendCode}
										disabled={request2faLoading}
										className="self-start text-xs text-neutral-400 transition-colors hover:text-neutral-100 disabled:opacity-50"
									>
										Reenviar código
									</button>
								)}
							</>
						)}

						{errorMessage && !codeConfirmedSuccessfully && <Notice message={errorMessage} variant="error" />}
					</>
				)}

				{codeConfirmedSuccessfully && <Notice message="Código verificado 👍" variant="success" />}
			</div>
		);
	}

	return (
		<div className={cn('flex flex-col gap-3', className)}>
			{/* Step 1: Select method */}
			{!selectedMethod && (
				<>
					<p className="text-sm text-neutral-600 dark:text-neutral-400">
						Selecciona un método para recibir tu código de verificación
					</p>
					<div className="flex flex-col gap-2">
						{methods.map((method, index) => {
							const color = getMethodColor(method.type);
							return (
								<Button
									key={index}
									variant="primary"
									className={cn(
										'w-full',
										color === 'blue' && '!bg-blue-600 hover:!bg-blue-700',
										color === 'green' && '!bg-green-600 hover:!bg-green-700',
										color === 'indigo' && '!bg-indigo-600 hover:!bg-indigo-700',
										color === 'purple' && '!bg-purple-600 hover:!bg-purple-700'
									)}
									onClick={() => handleMethodSelect(method)}
								>
									<div className="flex w-full items-center justify-between">
										<div className="flex items-center gap-2">
											{getMethodIcon(method.type)}
											<span>{getMethodLabel(method)}</span>
										</div>
									</div>
								</Button>
							);
						})}
					</div>
				</>
			)}

			{/* Step 2: Show input and send code */}
			{selectedMethod && !codeConfirmedSuccessfully && (
				<>
					{!codeSentSuccessfully && (
						<Button
							variant="ghost"
							className="w-fit text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
							onClick={() => handleMethodSelect(null as any)}
						>
							← Cambiar método
						</Button>
					)}

					{renderMethodInput()}

					{!codeSentSuccessfully && (
						<>
							{cooldown > 0 && (
								<p className="text-xs text-neutral-500">Debe esperar {cooldown} segundos para enviar otro código</p>
							)}
							<Button
								variant="primary"
								loading={request2faLoading}
								disabled={!canSendCode() || cooldown > 0 || request2faLoading}
								onClick={handleSendCode}
								className={cn(
									'w-full',
									selectedMethod.type === 'sms' && 'bg-blue-600 hover:bg-blue-700',
									selectedMethod.type === 'whatsapp' && 'bg-green-600 hover:bg-green-700',
									selectedMethod.type === 'email' && 'bg-indigo-600 hover:bg-indigo-700',
									selectedMethod.type === 'in_app' && 'bg-purple-600 hover:bg-purple-700'
								)}
							>
								<div className="flex w-full items-center justify-center gap-2">
									<SendHorizonal className="size-4" />
									<span>Enviar código</span>
								</div>
							</Button>
						</>
					)}

					{/* Step 3: Confirm code (if confirmCode is true) */}
					{confirmCode && codeSentSuccessfully && !codeConfirmedSuccessfully && (
						<>
							<Notice message="Código enviado exitosamente. Por favor revisa tu dispositivo." variant="success" />
							<Input
								readonly={codeConfirmedSuccessfully}
								disabled={confirm2faLoading || codeConfirmedSuccessfully}
								containerClassName="w-full"
								Icon={<KeyRound className="size-4 text-primary-500" />}
								value={code}
								onChange={(value) => setCode(value)}
								placeholder="Ingresa el código de 6 dígitos"
								allowedChars="numbers_only"
								floatingLabel="Código de verificación"
								maxLength={6}
							/>
							{cooldown <= 0 && (
								<Button
									variant="ghost"
									className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
									onClick={handleSendCode}
									disabled={request2faLoading}
								>
									Reenviar código
								</Button>
							)}
						</>
					)}

					{/* Success message without confirmation */}
					{!confirmCode && codeSentSuccessfully && <Notice message="Código enviado exitosamente." variant="success" />}

					{errorMessage && !codeConfirmedSuccessfully && <Notice message={errorMessage} variant="error" />}
				</>
			)}

			{/* Final success message */}
			{codeConfirmedSuccessfully && <Notice message="Código verificado exitosamente 👍" variant="success" />}
		</div>
	);
}
